import { ETelegramChannelType, ITelegramChannelId } from 'types';

import {
  ESettingsAliases,
  IGetSettingsResponse,
  ISetting,
  ISettingsContext,
  TTelegramNotificationsConfig,
} from '../interfaces';
import { getTaxes } from './getTaxes';
import { getValue } from './getValue';

export const getSettingsValues = (
  settingValues: Array<ISetting>,
  currencies: IGetSettingsResponse['currencies']
): ISettingsContext['state']['settings'] => {
  const clubName = getValue(settingValues, ESettingsAliases.CLUB_NAME, '');
  const whiteList = getValue<Array<string>>(
    settingValues,
    ESettingsAliases.SHELL_WHITE_LIST,
    []
  );
  const isWhiteListEnabled = getValue(
    settingValues,
    ESettingsAliases.SHELL_WHITE_LIST_ENABLED,
    false
  );

  const action = getValue(
    settingValues,
    ESettingsAliases.SHELL_END_SESSION_ACTION,
    3
  );

  const timeout =
    getValue(settingValues, ESettingsAliases.SHELL_END_SESSION_TIMEOUT, 180) /
    60;

  const lastUploadedFileId = getValue(
    settingValues,
    ESettingsAliases.SENET_USER_IMPORT,
    0
  );

  const autorun = getValue(settingValues, ESettingsAliases.SHELL_AUTORUN, []);

  const visibleDiscs = getValue(
    settingValues,
    ESettingsAliases.SHELL_VISIBLE_DISCS,
    '0x0'
  );

  const shouldUseClientDiscount = getValue(
    settingValues,
    ESettingsAliases.CLIENT_DISCOUNT,
    true
  );

  const shouldPrintReport = getValue(
    settingValues,
    ESettingsAliases.PRINT_REPORT,
    false
  );
  const printReportAllGoods = getValue(
    settingValues,
    ESettingsAliases.PRINT_REPORT_ALL_GOODS,
    false
  );

  const runPerMinuteOnLogin = getValue(
    settingValues,
    ESettingsAliases.SHELL_PER_MINUTE_ON_LOGIN,
    false
  );

  const runPerMinuteOnSessionEnd = getValue(
    settingValues,
    ESettingsAliases.SHELL_PER_MINUTE_ON_SESSION_END,
    false
  );

  const isChromeDownloadRestrict = getValue(
    settingValues,
    ESettingsAliases.SHELL_RESTRICT_CHROME_DOWNLOAD,
    false
  );

  const isRefundEnabled = getValue(
    settingValues,
    ESettingsAliases.REFUNDS,
    true
  );

  const refundHeadroom = getValue(
    settingValues,
    ESettingsAliases.REFUNDS_HEADROOM,
    5
  );

  const isDepositRefillEnabled = getValue(
    settingValues,
    ESettingsAliases.DEPOSIT_REFILL_ENABLED,
    true
  );

  const blacklist = getValue(
    settingValues,
    ESettingsAliases.DEFENCE_BLACKLIST,
    []
  );

  const bonusGoodsEnabled = getValue(
    settingValues,
    ESettingsAliases.BONUS_GOODS_ENABLED,
    false
  );

  const bonusServicesEnabled = getValue(
    settingValues,
    ESettingsAliases.BONUS_SERVICES_ENABLED,
    false
  );

  const hostMap = getValue(settingValues, ESettingsAliases.HOST_MAP, '');

  const telegramNotificationTypes = getValue<TTelegramNotificationsConfig>(
    settingValues,
    ESettingsAliases.TELEGRAM_NOTIFICATION_TYPES,
    {
      bonus: '0',
      workshifts: '0',
      cash_order: '0',
      refund: '0',
      shell_status: '0',
      user_deposit: '0',
      user_discount: '0',
      review: '1',
      security: '0',
      warehouse: '0',
      worker_auth: '0',
      low_stock: '1',
      loyalty_program: '0',
      advertisement: '0',
    }
  );

  const rawTelegramChannelId = getValue(
    settingValues,
    ESettingsAliases.TELEGRAM_CHANNEL_ID,
    ''
  );

  const telegramChannelId: ITelegramChannelId =
    typeof JSON.parse(rawTelegramChannelId) !== 'object'
      ? {
          id: rawTelegramChannelId == '-0' ? '' : rawTelegramChannelId,
          type: ETelegramChannelType.GROUP,
          title: '',
        }
      : JSON.parse(rawTelegramChannelId);

  const telegramToken = getValue<string>(
    settingValues,
    ESettingsAliases.TELEGRAM_TOKEN,
    ''
  );

  const isClubRulesEnabled = getValue(
    settingValues,
    ESettingsAliases.CLUB_RULES_ENABLED,
    false
  );

  const clubRules = getValue(
    settingValues,
    ESettingsAliases.CLUB_RULES_TEXT,
    ''
  );

  const externalStorages = getValue(
    settingValues,
    ESettingsAliases.EXTERNAL_STORAGES,
    false
  );

  // CASHBOX
  const isCashboxEnabled = getValue(
    settingValues,
    ESettingsAliases.CASHBOX,
    false
  );
  const selectedCashboxINN =
    getValue<string | null>(
      settingValues,
      ESettingsAliases.CASHBOX_SELECTED_INN,
      ''
    ) || null;
  const taxes = getTaxes(settingValues);

  const shellBgSkin =
    getValue<string>(settingValues, ESettingsAliases.SHELL_BG_SKIN, '') || null;

  const currencyValue = getValue(
    settingValues,
    ESettingsAliases.CURRENCY,
    'RUB'
  );

  const sign =
    currencies.find((currency) => currency.alias === currencyValue)?.letter ??
    '₽';

  const selfHostChange = getValue(
    settingValues,
    ESettingsAliases.SHELL_SELF_HOST_CHANGE,
    false
  );
  const hostGroupSwitch = getValue(
    settingValues,
    ESettingsAliases.SHELL_HOST_GROUP_SWITCH,
    false
  );
  const hostGroupSwitchPausable = getValue(
    settingValues,
    ESettingsAliases.SHELL_HOST_GROUP_SWITCH_PAUSABLE,
    false
  );

  const feedbackEnabled = getValue(
    settingValues,
    ESettingsAliases.FEEDBACK_ENABLED,
    false
  );

  const callAdmin = getValue(settingValues, ESettingsAliases.CALL_ADMIN, false);
  const callAdminTimeout =
    getValue(settingValues, ESettingsAliases.CALL_ADMIN_TIMEOUT, 900) / 60;

  const screenSaver =
    getValue<string>(settingValues, ESettingsAliases.SHELL_SCREENSAVER, '') ||
    null;

  const screenSaverTimeout =
    getValue(settingValues, ESettingsAliases.SHELL_SCREENSAVER_TIMEOUT, 300) /
    60;

  const autoTurnoffEnabled = getValue(
    settingValues,
    ESettingsAliases.AUTO_TURNOFF_ENABLED,
    false
  );

  const autoTurnoffTimeout =
    getValue(settingValues, ESettingsAliases.AUTO_TURNOFF_TIMEOUT, 600) / 60;

  const bookingStrictMode = getValue(
    settingValues,
    ESettingsAliases.BOOKING_STRICT_MODE,
    false
  );
  const bookingStrictModeHeadroom =
    getValue(
      settingValues,
      ESettingsAliases.BOOKING_STRICT_MODE_HEADROOM,
      300
    ) / 60;

  const bookingExpirationThreshold =
    getValue(
      settingValues,
      ESettingsAliases.BOOKING_EXPIRATION_THRESHOLD,
      900
    ) / 60;

  const remoteControlCCBoot = getValue(
    settingValues,
    ESettingsAliases.REMOTE_CONTROL_CCBOOT,
    false
  );
  const remoteControlEnabled = getValue(
    settingValues,
    ESettingsAliases.REMOTE_CONTROL_ENABLED,
    false
  );
  const remoteControlPassword = getValue(
    settingValues,
    ESettingsAliases.REMOTE_CONTROL_PASSWORD,
    ''
  );
  const removeControlPasswordUpdated =
    settingValues.find(
      (setting) => setting.alias === ESettingsAliases.REMOTE_CONTROL_PASSWORD
    )?.updated_at ?? null;

  const restrictVirtualHostDepositPayment = getValue(
    settingValues,
    ESettingsAliases.RESTRICT_VIRTUAL_HOST_DEPOSIT_PAYMENT,
    false
  );

  const shellLanguage = getValue(
    settingValues,
    ESettingsAliases.SHELL_LANGUAGE,
    'EN'
  ) as 'RU' | 'EN';

  const mouseUtil = getValue(settingValues, ESettingsAliases.MOUSE_UTIL, false);

  const videoUtil = getValue(settingValues, ESettingsAliases.VIDEO_UTIL, false);

  const soundUtil = getValue(settingValues, ESettingsAliases.SOUND_UTIL, false);

  const onlineBooking = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING,
    false
  );
  const onlineBookingCancel = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_CANCEL,
    false
  );
  const onlineBookingDurationMax =
    getValue(settingValues, ESettingsAliases.ONLINE_BOOKING_MAX_DURATION, 0) /
    3600;
  const onlineBookingDurationMin =
    getValue(settingValues, ESettingsAliases.ONLINE_BOOKING_MIN_DURATION, 0) /
    3600;
  const onlineBookingCancelUntil = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_CANCEL_UNTIL,
    0
  );
  const onlineBookingPenalty = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_PENALTY,
    0
  );
  const onlineBookingBusy = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_BUSY,
    false
  );

  const onlineBookingMultipleHost = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_MULTIPLE_HOST,
    false
  );

  const timezone = getValue(settingValues, ESettingsAliases.TIMEZONE, 0);

  const cloudPayments = getValue(
    settingValues,
    ESettingsAliases.CLOUD_PAYMENTS,
    false
  );

  const publicId = getValue(
    settingValues,
    ESettingsAliases.CLOUD_PAYMENTS_PUBLIC_ID,
    null
  );

  const holidayTariffsEnabled = getValue(
    settingValues,
    ESettingsAliases.HOLIDAY_TARIFFS_ENABLED,
    false
  );

  const holidayTariffsDates = getValue(
    settingValues,
    ESettingsAliases.HOLIDAY_TARIFFS_DATES,
    []
  );

  const tinkoffSbpEnabled = getValue(
    settingValues,
    ESettingsAliases.TINKOFF_SBP_ENABLED,
    false
  );
  const showSeans = getValue(
    settingValues,
    ESettingsAliases.SHOW_SEANS_ON_MAP,
    false
  );

  const postPaymentEnabled = getValue(
    settingValues,
    ESettingsAliases.POST_PAYMENT_ENABLED,
    false
  );
  const postPaymentMaxDuration =
    getValue(settingValues, ESettingsAliases.POST_PAYMENT_MAX_DURATION, 10800) /
    60;

  const onlineBookingPerMinuteEndingEnabled = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_PER_MINUTE_ENDING,
    false
  );

  const clientRatingEnabled = getValue(
    settingValues,
    ESettingsAliases.CLIENT_RATING_ENABLED,
    false
  );
  const clientRatingShellEnabled = getValue(
    settingValues,
    ESettingsAliases.CLIENT_RATING_SHELL_ENABLED,
    false
  );

  const isBonusPaymentTariffsEnabled = getValue(
    settingValues,
    ESettingsAliases.TARIFFS_ENABLED,
    false
  );
  const isUseDepositFirstEnabled = getValue(
    settingValues,
    ESettingsAliases.USE_DEPOSIT_FIRST_ENABLED,
    false
  );
  const isUsagePercent = getValue(
    settingValues,
    ESettingsAliases.USAGE_PERCENT,
    0
  );

  const shellGoodsDisplayEnabled = getValue(
    settingValues,
    ESettingsAliases.SHELL_GOODS_DISPLAY_ENABLED,
    false
  );

  const onlineBookingDiscount = getValue(
    settingValues,
    ESettingsAliases.ONLINE_BOOKING_DISCOUNT,
    0
  );

  const bookingBlockDuration =
    getValue(settingValues, ESettingsAliases.ONLINE_BOOKING_BLOCK_DURATION, 0) /
    60;

  const onlinePaymentsMinLimit = getValue(
    settingValues,
    ESettingsAliases.ONLINE_PAYMENTS_MIN_LIMIT,
    10
  );

  const countryCode = getValue(
    settingValues,
    ESettingsAliases.COUNTRY_CODE,
    'RU'
  );

  const cloudPaymentsVat = getValue(
    settingValues,
    ESettingsAliases.CLOUD_PAYMENTS_VAT,
    null
  );

  const cashierName = getValue(
    settingValues,
    ESettingsAliases.CASHIER_NAME,
    ''
  );
  const depositRefillTitle = getValue(
    settingValues,
    ESettingsAliases.DEPOSIT_REFILL_TITLE,
    null
  );

  const lowStockEnabled = getValue(
    settingValues,
    ESettingsAliases.LOW_STOCK_ENABLED,
    false
  );

  const lowStockThreshold = getValue(
    settingValues,
    ESettingsAliases.LOW_STOCK_THRESHOLD,
    5
  );

  const isBonusSystemEnabled = getValue(
    settingValues,
    ESettingsAliases.BONUS_SYSTEM_ENABLED,
    false
  );

  const clientRegister = getValue(
    settingValues,
    ESettingsAliases.CLIENT_REGISTER,
    false
  );

  const registerWithoutVerification = getValue(
    settingValues,
    ESettingsAliases.REGISTER_WITHOUT_VERIFICATION,
    false
  );

  const kaspiOnlineEnabled = getValue(
    settingValues,
    ESettingsAliases.KASPI_ONLINE_ENABLED,
    false
  );

  const kaspiOnlineBin = getValue(
    settingValues,
    ESettingsAliases.KASPI_ONLINE_BIN,
    null
  );

  const stripeEnabled = getValue(
    settingValues,
    ESettingsAliases.STRIPE_ENABLED,
    false
  );
  const stripeSecretKey = getValue(
    settingValues,
    ESettingsAliases.STRIPE_SECRET_KEY,
    ''
  );
  const stripePublishableKey = getValue(
    settingValues,
    ESettingsAliases.STRIPE_PUBLISHABLE_KEY,
    ''
  );

  const bePaidEnabled = getValue(
    settingValues,
    ESettingsAliases.BE_PAID_ENABLED,
    false
  );
  const bePaidSecretKey = getValue(
    settingValues,
    ESettingsAliases.BE_PAID_SECRET_KEY,
    ''
  );
  const bePaidPublishableKey = getValue(
    settingValues,
    ESettingsAliases.BE_PAID_PUBLISHABLE_KEY,
    ''
  );
  const bePaidShopId = getValue(
    settingValues,
    ESettingsAliases.BE_PAID_SHOP_ID,
    ''
  );

  const tipsValues = getValue(settingValues, ESettingsAliases.TIPS_VALUES, []);

  const tipsEnabled = getValue(
    settingValues,
    ESettingsAliases.TIPS_ENABLED,
    false
  );

  const screenSmartKiosk = getValue(
    settingValues,
    ESettingsAliases.SHELL_SMART_KIOSK,
    null
  );
  const screenSmartTV = getValue(
    settingValues,
    ESettingsAliases.SHELL_SMART_TV,
    null
  );

  const shellSkinAccentColor = getValue(
    settingValues,
    ESettingsAliases.SHELL_SKIN_ACCENT_COLOR,
    '#7C62FF'
  );

  const shellSkinAdditionalColor = getValue(
    settingValues,
    ESettingsAliases.SHELL_SKIN_ADDITIONAL_COLOR,
    '#000000'
  );

  const shellSkinBackgroundColor = getValue(
    settingValues,
    ESettingsAliases.SHELL_SKIN_BACKGROUND_COLOR,
    shellBgSkin ? '' : '#232323'
  );

  const shellSkinLogo = getValue(
    settingValues,
    ESettingsAliases.SHELL_SKIN_LOGO,
    ''
  );

  const fastLoginAutostartSessionEnabled = getValue(
    settingValues,
    ESettingsAliases.SHELL_FAST_LOGIN_AUTOSTART_SESSION_ENABLED,
    false
  );

  const fastLoginAutostartSessionTariffId = getValue(
    settingValues,
    ESettingsAliases.SHELL_FAST_LOGIN_AUTOSTART_SESSION_TARIFF_ID,
    null
  );

  const hardwareChanges = getValue(
    settingValues,
    ESettingsAliases.HARDWARE_CHANGED,
    false
  );

  const lightShell = getValue(
    settingValues,
    ESettingsAliases.LITE_SHELL,
    false
  );

  return {
    autorun,
    clubName,
    whiteList,
    isWhiteListEnabled,
    action,
    timeout,
    lastUploadedFileId,
    visibleDiscs,
    runPerMinuteOnLogin,
    runPerMinuteOnSessionEnd,
    isChromeDownloadRestrict,
    isRefundEnabled,
    refundHeadroom,
    shouldUseClientDiscount,
    shouldPrintReport,
    printReportAllGoods,
    isCashboxEnabled,
    selectedCashboxINN,
    taxes,
    isDepositRefillEnabled,
    blacklist,
    telegramNotificationTypes,
    telegramChannelId,
    telegramToken,
    isClubRulesEnabled,
    clubRules,
    externalStorages,
    shellBgSkin,
    currency: {
      value: currencyValue,
      sign,
    },
    selfHostChange,
    hostGroupSwitch,
    hostGroupSwitchPausable,
    feedbackEnabled,
    screenSaver,
    screenSaverTimeout,
    callAdmin,
    callAdminTimeout,
    autoTurnoffEnabled,
    autoTurnoffTimeout,
    bookingStrictMode,
    bookingStrictModeHeadroom,
    bookingExpirationThreshold,
    remoteControlCCBoot,
    remoteControlEnabled,
    remoteControlPassword,
    removeControlPasswordUpdated,
    restrictVirtualHostDepositPayment,
    shellLanguage,
    videoUtil,
    soundUtil,
    mouseUtil,
    onlineBooking,
    onlineBookingDurationMin,
    onlineBookingDurationMax,
    onlineBookingCancel,
    onlineBookingCancelUntil,
    onlineBookingPenalty,
    onlineBookingBusy,
    onlineBookingMultipleHost,
    timezone,
    cloudPayments,
    publicId,
    holidayTariffsEnabled,
    holidayTariffsDates,
    tinkoffSbpEnabled,
    showSeans,
    postPaymentEnabled,
    postPaymentMaxDuration,
    onlineBookingPerMinuteEndingEnabled,
    clientRatingEnabled,
    isBonusPaymentTariffsEnabled,
    isUseDepositFirstEnabled,
    isUsagePercent,
    shellGoodsDisplayEnabled,
    onlineBookingDiscount,
    bookingBlockDuration,
    onlinePaymentsMinLimit,
    lowStockThreshold,
    bonusGoodsEnabled,
    bonusServicesEnabled,
    countryCode,
    cloudPaymentsVat,
    cashierName,
    depositRefillTitle,
    lowStockEnabled,
    isBonusSystemEnabled,
    clientRegister,
    registerWithoutVerification,
    kaspiOnlineEnabled,
    kaspiOnlineBin,
    stripeEnabled,
    stripeSecretKey,
    stripePublishableKey,
    screenSmartKiosk,
    screenSmartTV,
    shellSkinAccentColor,
    shellSkinAdditionalColor,
    shellSkinBackgroundColor,
    shellSkinLogo,
    tipsValues,
    tipsEnabled,
    fastLoginAutostartSessionEnabled,
    fastLoginAutostartSessionTariffId,
    hostMap,
    hardwareChanges,
    lightShell,
    clientRatingShellEnabled,
    bePaidEnabled,
    bePaidSecretKey,
    bePaidPublishableKey,
    bePaidShopId,
  };
};
